import { CircleProgress } from "@peer-start/ui/reducate";
import { useTranslation } from "react-i18next";

function meterColor(percent: number): string {
  if (percent < 50) return "var(--destructive-solid)";
  if (percent < 80) return "var(--chart-2)";
  return "var(--chart-4)";
}

/** CircleProgress has no chart-colour variant, so the ring inherits the same theme stroke as the detail page. */
function meterToneClass(percent: number): string {
  if (percent < 50) return "text-[var(--destructive-solid)]";
  if (percent < 80) return "text-[var(--chart-2)]";
  return "text-[var(--chart-4)]";
}

function GroundednessRing({
  percent,
  className,
  radius = 15.5,
  strokeWidth = 3,
}: {
  percent: number;
  className: string;
  radius?: number;
  strokeWidth?: number;
}) {
  const circumference = 2 * Math.PI * radius;
  const dash = (percent / 100) * circumference;
  return (
    <svg aria-hidden="true" className={`${className} -rotate-90`} viewBox="0 0 36 36">
      <circle cx="18" cy="18" fill="none" r={radius} stroke="var(--muted)" strokeWidth={strokeWidth} />
      <circle
        cx="18"
        cy="18"
        fill="none"
        r={radius}
        stroke={meterColor(percent)}
        strokeDasharray={`${dash} ${circumference}`}
        strokeLinecap="round"
        strokeWidth={strokeWidth}
        style={{ transition: "stroke 600ms ease, stroke-dasharray 600ms ease" }}
      />
    </svg>
  );
}

function meterLabel(percent: number, t: (key: string, options?: { percent: number }) => string) {
  return {
    "aria-label": t("administration.situationCard.groundedness"),
    "aria-valuemax": 100,
    "aria-valuemin": 0,
    "aria-valuenow": percent,
    "aria-valuetext": t("administration.situationCard.groundednessValue", { percent }),
    role: "meter" as const,
  };
}

export function SituationGroundednessMeter({ percent }: { percent: number }) {
  const { t } = useTranslation();
  return (
    <span {...meterLabel(percent, t)}>
      <CircleProgress className={meterToneClass(percent)} size="sm" value={percent} variant="current" />
    </span>
  );
}

export function SituationGroundedness({ percent }: { percent: number }) {
  const { t } = useTranslation();
  return (
    <div {...meterLabel(percent, t)} className="flex items-center gap-4 rounded-xl border border-border bg-background px-4 py-3">
      <div className="relative grid size-16 shrink-0 place-items-center">
        <GroundednessRing className="size-16" percent={percent} />
        <span className="absolute text-sm font-medium tabular-nums">{percent}%</span>
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium">{t("administration.situationCard.groundedness")}</p>
        <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
          {t("administration.situationCard.groundednessHint")}
        </p>
      </div>
    </div>
  );
}
